import React, { useEffect, useState } from "react";
import { UserPlus, Check, X, Search, MessageCircle, CheckCircle2, Circle, Send, Users2 } from "lucide-react";
import api from "@/lib/api";
import Modal from "@/components/chat/Modal";
import { getInitials, avatarColor } from "@/lib/avatar";
import LiveRing from "@/components/live/LiveRing";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

function Row({ u, onOpenProfile, right, selectMode, selected, onToggle }) {
  const click = () => (selectMode ? onToggle(u.id) : onOpenProfile(u.id));
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`friend-row-${u.id}`}>
      <LiveRing uid={u.id}>
        <button onClick={click} className="h-12 w-12 rounded-2xl grid place-items-center text-[var(--rc-gold)] font-semibold border-2 border-[var(--rc-gold)] overflow-hidden shrink-0" style={{ backgroundColor: u.avatar ? "var(--rc-bg)" : avatarColor(u.name) }}>
          {u.avatar ? <img src={u.avatar} alt="" className="h-full w-full object-cover" /> : getInitials(u.name)}
        </button>
      </LiveRing>
      <button onClick={click} className="min-w-0 flex-1 text-left">
        <div className="font-medium truncate">{u.name} {u.flag}</div>
        {u.about && <div className="text-xs text-[var(--rc-muted)] truncate">{u.about}</div>}
      </button>
      {selectMode
        ? <button onClick={() => onToggle(u.id)} data-testid={`friend-select-${u.id}`} className="text-[var(--rc-gold)]">{selected ? <CheckCircle2 size={24} /> : <Circle size={24} className="text-[var(--rc-muted)]" />}</button>
        : right}
    </div>
  );
}

export default function FriendsPage({ onOpenProfile, onOpenThread }) {
  const { t } = useI18n();
  const [tab, setTab] = useState("friends"); // friends | followers | following | requests | discover
  const [friends, setFriends] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [following, setFollowing] = useState([]);
  const [requests, setRequests] = useState([]);
  const [discover, setDiscover] = useState([]);
  const [q, setQ] = useState("");
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState({});
  const [compose, setCompose] = useState(false);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const loadFriends = () => api.get("/social/friends").then(({ data }) => setFriends(data)).catch(() => {});
  const loadFollowers = () => api.get("/social/followers").then(({ data }) => setFollowers(data)).catch(() => {});
  const loadFollowing = () => api.get("/social/following").then(({ data }) => setFollowing(data)).catch(() => {});
  const loadRequests = () => api.get("/social/friends/requests").then(({ data }) => setRequests(data)).catch(() => {});
  const loadDiscover = (query = "") => api.get("/social/discover", { params: query ? { q: query } : {} }).then(({ data }) => setDiscover(data)).catch(() => {});

  useEffect(() => { loadFriends(); loadFollowers(); loadFollowing(); loadRequests(); loadDiscover(); }, []);
  useEffect(() => { if (tab === "discover") { const tm = setTimeout(() => loadDiscover(q), 300); return () => clearTimeout(tm); } }, [q, tab]);
  useEffect(() => { setSelectMode(false); setSelected({}); }, [tab]);

  const respond = async (uid, action) => {
    try { await api.post(`/social/friends/respond/${uid}`, { action }); setRequests((r) => r.filter((x) => x.id !== uid)); if (action === "accept") { loadFriends(); toast.success(t("s_friend_added")); } }
    catch { toast.error(t("s_failed")); }
  };
  const addFriend = async (uid) => {
    try { await api.post(`/social/friends/request/${uid}`); setDiscover((d) => d.map((x) => x.id === uid ? { ...x, requested: true } : x)); toast.success(t("s_request_sent")); }
    catch { toast.error(t("s_failed")); }
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);
  const currentList = tab === "friends" ? friends : tab === "followers" ? followers : following;
  const selectAll = () => setSelected(Object.fromEntries(currentList.map((u) => [u.id, true])));

  const sendBroadcast = async () => {
    if (!msg.trim() || !selectedIds.length) return;
    try {
      setSending(true);
      const { data } = await api.post("/social/broadcast", { user_ids: selectedIds, text: msg.trim() });
      toast.success(t("s_broadcast_sent").replace("{n}", data.sent));
      setCompose(false); setMsg(""); setSelectMode(false); setSelected({});
    } catch { toast.error(t("s_failed")); } finally { setSending(false); }
  };

  const tabs = [
    ["friends", t("s_friends"), friends.length],
    ["followers", t("s_followers"), followers.length],
    ["following", t("s_following"), following.length],
    ["requests", t("s_requests"), requests.length],
    ["discover", t("s_discover"), null],
  ];
  const selectable = tab === "friends" || tab === "followers" || tab === "following";

  return (
    <div data-testid="friends-page">
      <div className="flex gap-1 overflow-x-auto no-scrollbar rounded-full bg-[var(--rc-surface)] p-1 mb-4">
        {tabs.map(([k, l, n]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`friends-tab-${k}`}
            className={`shrink-0 rounded-full px-3.5 py-2 text-xs font-medium whitespace-nowrap transition-colors ${tab === k ? "bg-[var(--rc-gold)]/20 text-[var(--rc-gold)]" : "text-[var(--rc-muted)]"}`}>
            {l}{n ? ` (${n})` : ""}
          </button>
        ))}
      </div>

      {selectable && currentList.length > 0 && (
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => { setSelectMode((v) => !v); setSelected({}); }} data-testid="friends-select-toggle"
            className="flex items-center gap-1.5 text-xs font-medium text-[var(--rc-gold)]">
            <Users2 size={15} /> {selectMode ? t("s_cancel") : t("s_select")}
          </button>
          {selectMode && <button onClick={selectAll} data-testid="friends-select-all" className="text-xs text-[var(--rc-muted)]">{t("s_select_all")}</button>}
        </div>
      )}

      {tab === "friends" && (friends.length ? <List items={friends} {...{ onOpenProfile, onOpenThread, selectMode, selected, toggle }} /> : <Empty text={t("s_no_friends")} />)}
      {tab === "followers" && (followers.length ? <List items={followers} {...{ onOpenProfile, onOpenThread, selectMode, selected, toggle }} /> : <Empty text={t("s_no_followers")} />)}
      {tab === "following" && (following.length ? <List items={following} {...{ onOpenProfile, onOpenThread, selectMode, selected, toggle }} /> : <Empty text={t("s_no_following")} />)}

      {tab === "requests" && (requests.length ? <div className="divide-y divide-[var(--rc-border)]">{requests.map((u) => (
        <Row key={u.id} u={u} onOpenProfile={onOpenProfile} right={
          <div className="flex gap-2">
            <button onClick={() => respond(u.id, "accept")} data-testid={`friend-accept-${u.id}`} className="h-10 w-10 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Check size={17} /></button>
            <button onClick={() => respond(u.id, "reject")} data-testid={`friend-reject-${u.id}`} className="h-10 w-10 grid place-items-center rounded-full border border-[var(--rc-border)] text-[var(--rc-muted)]"><X size={17} /></button>
          </div>
        } />
      ))}</div> : <Empty text={t("s_no_requests")} />)}

      {tab === "discover" && (
        <>
          <div className="flex items-center gap-2 rounded-full bg-[var(--rc-surface)] border border-[var(--rc-border)] px-4 mb-3">
            <Search size={17} className="text-[var(--rc-muted)]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("s_search_people")} data-testid="discover-search"
              className="flex-1 bg-transparent py-2.5 text-sm focus:outline-none" />
          </div>
          {discover.length ? <div className="divide-y divide-[var(--rc-border)]">{discover.map((u) => (
            <Row key={u.id} u={u} onOpenProfile={onOpenProfile} right={
              u.is_friend
                ? <span className="text-xs text-[var(--rc-gold)] font-medium">{t("s_friends")}</span>
                : <button onClick={() => addFriend(u.id)} disabled={u.requested} data-testid={`discover-add-${u.id}`} className="flex items-center gap-1.5 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-3.5 py-2 text-xs disabled:opacity-40"><UserPlus size={15} /> {u.requested ? t("s_sent") : t("s_add")}</button>
            } />
          ))}</div> : <Empty text={t("s_no_people")} />}
        </>
      )}

      {selectMode && selectedIds.length > 0 && (
        <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30">
          <button onClick={() => setCompose(true)} data-testid="friends-message-selected"
            className="flex items-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold px-5 py-3 shadow-xl">
            <Send size={16} /> {t("s_message_selected").replace("{n}", selectedIds.length)}
          </button>
        </div>
      )}

      {compose && (
        <Modal title={t("s_message_selected").replace("{n}", selectedIds.length)} onClose={() => setCompose(false)}>
          <div className="space-y-3" data-testid="broadcast-composer">
            <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={4} placeholder={t("s_write_message")} data-testid="broadcast-text"
              className="w-full bg-[var(--rc-bg)] border border-[var(--rc-border)] rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:border-[var(--rc-gold)]/50" />
            <button onClick={sendBroadcast} disabled={sending || !msg.trim()} data-testid="broadcast-send"
              className="w-full rounded-xl bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-40 flex items-center justify-center gap-2">
              <Send size={16} /> {t("s_send")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function List({ items, onOpenProfile, onOpenThread, selectMode, selected, toggle }) {
  return (
    <div className="divide-y divide-[var(--rc-border)]">
      {items.map((u) => (
        <Row key={u.id} u={u} onOpenProfile={onOpenProfile} selectMode={selectMode} selected={!!selected[u.id]} onToggle={toggle}
          right={<button onClick={() => onOpenThread(u)} data-testid={`friend-message-${u.id}`} className="h-10 w-10 grid place-items-center rounded-full border border-[var(--rc-gold)]/40 text-[var(--rc-gold)]"><MessageCircle size={17} /></button>} />
      ))}
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-center py-16 text-[var(--rc-muted)]">{text}</div>;
}
